// Sessão do painel Super Master — provider próprio, montado SÓ nas rotas
// /master/* (ver __root.tsx), nunca junto do AuthProvider de empresa.
// Mesmo formato dele (recupera sessão no boot via cookie de refresh,
// access token só em memória) mas bem mais simples: uma conta só por vez,
// sem impersonação, sem sessão dupla Traccar+backend.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { GPSDRIVE_API_URL } from "@/lib/api/gpsdrive-client";
import { setPlatformSessionExpiredHandler } from "@/lib/api/gpsdrive-platform-client";
import { gpsDrivePlatformAuthService, type PlatformAdmin } from "@/services/gpsdrive-platform-auth.service";

export type PlatformSessionStatus = "loading" | "ok" | "expired";

interface PlatformAuthContextValue {
  admin: PlatformAdmin | null;
  status: PlatformSessionStatus;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const PlatformAuthContext = createContext<PlatformAuthContextValue | undefined>(undefined);

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<PlatformAdmin | null>(null);
  const [status, setStatus] = useState<PlatformSessionStatus>("loading");

  useEffect(() => {
    if (!GPSDRIVE_API_URL) {
      setStatus("expired");
      return;
    }
    let cancelled = false;
    gpsDrivePlatformAuthService
      .recoverSession()
      .then((a) => {
        if (cancelled) return;
        setAdmin(a);
        setStatus(a ? "ok" : "expired");
      })
      .catch(() => {
        if (!cancelled) setStatus("expired");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPlatformSessionExpiredHandler(() => {
      setAdmin(null);
      setStatus("expired");
    });
    return () => setPlatformSessionExpiredHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const a = await gpsDrivePlatformAuthService.login(email, password);
    setAdmin(a);
    setStatus("ok");
  }, []);

  const logout = useCallback(async () => {
    await gpsDrivePlatformAuthService.logout();
    setAdmin(null);
    setStatus("expired");
  }, []);

  return (
    <PlatformAuthContext.Provider value={{ admin, status, login, logout }}>
      {children}
    </PlatformAuthContext.Provider>
  );
}

export function usePlatformAuth(): PlatformAuthContextValue {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error("usePlatformAuth precisa estar dentro de <PlatformAuthProvider>");
  return ctx;
}
